import React, { useContext, useEffect, useState } from "react";

import { Box, CircularProgress } from "@material-ui/core";
import { getJWTTokenExp } from "../utils/jwt";
import { getCurrentTimeInSeconds } from "../utils/time";
import { ONE_MIN, refreshAccessToken } from "../utils/customHeader";
import graphQLClient from "../utils/authHeader";
import { LOCAL_STORAGE_ACCESS_TOKEN_KEY } from "../constants/localstorage";
import { eventBus } from "../utils/event";

/**
 * The AppRefreshTokenProvider is a wrapper and it gets executed on refresh
 * - it checks if jwt is still active otherwise try refreshing the accessToken
 * - it logs out the user if refresh token has expired or something goes wrong
 */

const AppRefreshTokenContext = React.createContext<{
  accessToken: string;
  refreshToken: string;
}>({
  accessToken: "",
  refreshToken: "",
});

const { Provider, Consumer } = AppRefreshTokenContext;

export const AppRefreshTokenProvider = (props: { children: any }) => {
  const [loading, setLoading] = useState(true);

  const refreshHandler = async () => {
    try {
      const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
      // Assuming you store token expiration in localStorage or decode JWT token
      if (accessToken) {
        const tokenExpiration = getJWTTokenExp(accessToken);
        const isTokenExpired =
          getCurrentTimeInSeconds() > Number(tokenExpiration) - ONE_MIN; // 1 min before expiration
        if (isTokenExpired) {
          const newAccessToken = await refreshAccessToken();
          graphQLClient.setHeader("authorization", `Bearer ${newAccessToken}`);
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      eventBus.emit("logout");
      console.error("Error refreshing token:", error);
    }
  };

  useEffect(() => {
    refreshHandler();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height='100vh'
      >
        <CircularProgress
          style={{
            color: "#11302D",
            height: "5rem",
            width: "5rem",
          }}
          thickness={5}
        />
      </Box>
    );
  }

  const value = {
    accessToken: "",
    refreshToken: "",
  };

  return <Provider value={value}>{props?.children}</Provider>;
};

export const useRefreshToken = () => useContext(AppRefreshTokenContext);

export { Consumer as TaskManagerConsumer };
export default AppRefreshTokenContext;
